import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Home as HomeIcon, Store, Briefcase, ArrowLeft, ArrowRight, Check,
  Calendar as CalendarIcon, Clock, User, Phone, Mail, Sparkles, Sun, Sunset, Zap,
} from "lucide-react";
import { toast } from "sonner";
import BookingAddress, { validAddress } from "../components/BookingAddress";
import ServicePrice from "../components/ServicePrice";
import { useCatalog } from "../contexts/CatalogContext";
import { useCustomer } from "../contexts/CustomerContext";
import { API } from "../lib/api";
const STORAGE_KEY = "repairro_booking_draft_v1";
const TOTAL = 4;
const STEP_NAMES = ["Service", "Address & contact", "Schedule", "Review"];

const SEGMENT_OPTIONS = [
  { id: "home", name: "Home", icon: HomeIcon, desc: "Family appliance service for your home." },
  { id: "shop", name: "Shop / Office", icon: Store, desc: "Commercial units, bulk service contracts." },
  { id: "professional", name: "Working Professional", icon: Briefcase, desc: "Evening & weekend slots, fast booking." },
];

const Stepper = ({ step, onSelect }) => (
  <nav aria-label="Booking progress" className="mb-8" data-testid="booking-stepper">
    <ol className="grid grid-cols-4 gap-2 sm:gap-4">
      {STEP_NAMES.map((name, index) => <li key={name}>
        <button data-testid={`booking-step-${index + 1}`} disabled={index + 1 >= step} onClick={() => onSelect(index + 1)} aria-current={index + 1 === step ? "step" : undefined} className={`w-full text-left text-xs sm:text-sm ${index + 1 <= step ? "text-sky-800" : "text-slate-400"}`}>
          <span className={`mb-2 flex h-8 w-8 items-center justify-center rounded-full font-semibold ${index + 1 <= step ? "bg-sky-800 text-white" : "bg-slate-100"}`}>{index + 1 < step ? <Check size={16} /> : index + 1}</span>
          <span className="font-medium">{name}</span>
          <span className={`mt-3 block h-1 rounded-full ${index + 1 <= step ? "bg-sky-700" : "bg-slate-200"}`} />
        </button>
      </li>)}
    </ol>
  </nav>
);

const dateOptions = () => {
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const day = d.toLocaleDateString("en-IN", { weekday: "short" });
    const dnum = d.getDate();
    const month = d.toLocaleDateString("en-IN", { month: "short" });
    out.push({
      iso,
      day,
      dnum,
      month,
      label: getDateLabel(i),
    });
  }
  return out;
};

const getDateLabel = (i) => {
  if (i === 0) return "Today";
  if (i === 1) return "Tomorrow";
  return null;
};

const slotIcon = (id) => {
  if (id === "morning") return Sun;
  if (id === "afternoon") return Sun;
  if (id === "evening") return Sunset;
  return Zap;
};

const dateBtnClass = (active) => {
  if (active) return "border-[#0C4A6E] bg-[#0C4A6E] text-white";
  return "border-slate-200 hover:border-slate-300 text-slate-700";
};

const slotBtnClass = (active) => {
  if (active) return "border-[#0C4A6E] bg-[#0C4A6E] text-white";
  return "border-slate-200 hover:border-slate-300 text-slate-700";
};

const slotIconClass = (active) => {
  if (active) return "text-white";
  return "text-slate-500";
};

export default function Booking() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const { customer, token, isAuthenticated } = useCustomer();
  const [addressReady, setAddressReady] = useState(false);
  const [validation, setValidation] = useState("");

  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        return { customer_type: "home", category: "", service: "", brand: "", address: "", city: "lucknow", pincode: "", landmark: "", date: "", slot: "", name: "", phone: "", email: "", notes: "", ...Object.fromEntries(["customer_type", "category", "service", "brand", "date", "slot"].filter(key => typeof draft[key] === "string").map(key => [key, draft[key]])) };
      }
    } catch (err) {
      // Draft restore failed (corrupt JSON) — fall through to default state.
    }
    return {
      customer_type: "home",
      category: "",
      service: "",
      brand: "",
      address: "",
      city: "lucknow",
      pincode: "",
      landmark: "",
      date: "",
      slot: "",
      name: "",
      phone: "",
      email: "",
      notes: "",
    };
  });

  // Preselect from URL params (only once on mount) — intentionally one-shot
  useEffect(() => {
    const cat = params.get("category");
    const svc = params.get("service");
    const seg = params.get("segment");
    setData((d) => ({
      ...d,
      ...(SEGMENT_OPTIONS.some(option => option.id === seg) ? { customer_type: seg } : {}),
      ...(cat && !d.category ? { category: cat } : {}),
      ...(svc && !d.service ? { service: svc } : {}),
    }));
  }, [params]);

  const { getCategoryById, getServiceById, categories, loading: catalogLoading, error: catalogError, SLOTS, CITIES } = useCatalog();
  const bookingCategories = useMemo(() => {
    const roCategory = categories.find((c) => c.id === "ro");
    if (!roCategory) return categories;
    return [roCategory, ...categories.filter((c) => c.id !== "ro")];
  }, [categories]);

  useEffect(() => {
    const available = categories.filter(c => c.status !== "soon");
    if (available.length === 1) setData(d => d.category === available[0].id ? d : { ...d, category: available[0].id, service: !d.category ? d.service : "", brand: !d.category ? d.brand : "" });
  }, [categories]);

  useEffect(() => {
    if (customer?.phone || customer?.name) {
      setData((d) => ({
        ...d,
        phone: customer?.phone?.replace(/\D/g, "").slice(-10) || "",
        name: customer?.name || d.name || "",
      }));
    }
  }, [customer?.phone, customer?.name]);

  // Auto-save draft (non-sensitive form preferences only — no PII beyond what user is about to submit)
  useEffect(() => {
    try {
      const { customer_type, category, service, brand, date, slot } = data;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ customer_type, category, service, brand, date, slot }));
    } catch (err) {
      // Storage quota / blocked — drafts simply won't persist, not fatal.
    }
  }, [data]);

  const category = getCategoryById(data.category);
  const service = getServiceById(data.category, data.service);

  const update = useCallback((patch) => setData((d) => ({ ...d, ...patch })), []);

  const serviceValid = !!service && category?.status !== "soon" && [...(category?.brands || []), "Other"].includes(data.brand) && SEGMENT_OPTIONS.some(option => option.id === data.customer_type);
  const addressValid = validAddress(data) && CITIES.some(c => c.id === data.city && c.status !== "soon") && (!isAuthenticated || addressReady);
  const contactValid = data.name.trim().length > 1 && /^\d{10}$/.test(data.phone.replace(/\D/g, "")) && (!data.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email));
  const scheduleValid = dateOptions().some(d => d.iso === data.date) && SLOTS.some(s => s.id === data.slot);
  const canNext = () => [serviceValid, addressValid && contactValid, scheduleValid, serviceValid && addressValid && contactValid && scheduleValid][step - 1];
  const messages = ["Choose your service, brand and customer type.", "Save or select a service address, enter your name and a valid 10-digit phone number. Enter a valid 6-digit pincode and check the email if provided.", "Choose an available date and time slot."];

  const next = () => {
    if (!canNext()) {
      setValidation(messages[step - 1]);
      return;
    }
    setValidation("");
    setStep((s) => Math.min(s + 1, TOTAL));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setValidation("");
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (submitting) return;
    if (!canNext()) { setValidation("Please review your service, address, contact details and schedule before confirming."); return; }
    if (catalogLoading || catalogError || !service) {
      toast.error("Please wait for current service prices or reload to try again.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        service_name: service?.name || "",
        price: service?.price ?? 0,
        expected_price: service.price,
      };
      const res = await axios.post(`${API}/bookings`, payload, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      localStorage.removeItem(STORAGE_KEY);
      navigate(`/booking/success/${res.data.booking_id}`, { state: { booking: res.data } });
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (err.response?.status === 409) {
        window.dispatchEvent(new Event("catalog-updated"));
        toast.error("Service price changed. Please review the updated price and confirm again.");
      } else {
        toast.error("Could not confirm booking. Please try again or call us.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="text-center mb-8">
        <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">Book a Service</span>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">Tell us what you need</h1>
        <p className="mt-2 text-slate-600 text-sm">Four simple steps. Pay only after the job is done.</p>
      </div>

      <Stepper step={step} onSelect={(value) => { setStep(value); setValidation(""); }} />
      {catalogLoading && <p role="status" className="mb-4 text-sm text-slate-500">Loading services and current prices…</p>}
      {catalogError && <p role="alert" className="mb-4 rounded-xl bg-red-50 p-4 text-sm text-red-700">Could not load current prices. <button data-testid="booking-reload-catalog" className="underline" onClick={() => window.dispatchEvent(new Event("catalog-updated"))}>Retry</button></p>}

      <div className="bg-white rounded-2xl border border-slate-200 card-shadow p-5 sm:p-8 space-y-8">
        {/* STEP 1: Customer Type */}
        {step === 1 && (
          <div data-testid="step-1">
            <h2 className="font-display font-bold text-xl text-slate-900">Who is this booking for?</h2>
            <p className="text-sm text-slate-500 mt-1">We tailor the experience based on your needs.</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {SEGMENT_OPTIONS.map((s) => {
                const Icon = s.icon;
                const active = data.customer_type === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`segment-option-${s.id}`}
                    onClick={() => update({ customer_type: s.id })}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${
                      active ? "border-[#0C4A6E] bg-[#0C4A6E]/5" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${active ? "text-[#0369A1]" : "text-slate-700"}`} />
                    <div className="font-display font-bold text-slate-900 mt-3">{s.name}</div>

                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2: Category */}
        {step === 1 && bookingCategories.filter(c => c.status !== "soon").length !== 1 && (
          <div data-testid="step-2">
            <h2 className="font-display font-bold text-xl text-slate-900">Which RO service do you need?</h2>
            <p className="text-sm text-slate-500 mt-1">We are currently focusing on RO services. More categories will be available soon.</p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {bookingCategories.map((c) => {
                const Icon = c.icon;
                const active = data.category === c.id;
                const disabled = c.status === "soon";
                return (
                  <button
                    key={c.id}
                    data-testid={`booking-category-${c.id}`}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      update({ category: c.id, service: "", brand: "" });
                    }}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      active ? "border-[#0C4A6E] bg-[#0C4A6E]/5" : "border-slate-200 hover:border-slate-300"
                    } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: c.bg, color: c.color }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-display font-bold text-slate-900 mt-3 text-sm">{c.short}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{disabled ? "Coming soon" : <>From <ServicePrice service={{ ...c, price: c.startingPrice }} /></>}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 3: Service */}
        {step === 1 && category && (
          <div data-testid="step-3">
            <h2 className="font-display font-bold text-xl text-slate-900">Pick the {category.short.toLowerCase()} service</h2>
            <div className="mt-5 space-y-3">
              {category.services.map((s) => {
                const active = data.service === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`booking-service-${s.id}`}
                    onClick={() => update({ service: s.id })}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-start justify-between gap-4 ${
                      active ? "border-[#0C4A6E] bg-[#0C4A6E]/5" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{s.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                      <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {s.time}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-lg text-[#0C4A6E]">
                        <ServicePrice service={s} />
                        {s.id.includes("amc") && <span className="text-xs font-medium text-slate-500">/yr</span>}
                      </div>
                      <div className="text-[10px] text-slate-500">Starting from</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 4: Brand */}
        {step === 1 && category && (
          <div data-testid="step-4">
            <h2 className="font-display font-bold text-xl text-slate-900">Which brand?</h2>
            <p className="text-sm text-slate-500 mt-1">Don't see your brand? Pick "Other".</p>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[...category.brands, "Other"].map((b) => {
                const active = data.brand === b;
                return (
                  <button
                    key={b}
                    data-testid={`booking-brand-${b.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}`}
                    onClick={() => update({ brand: b })}
                    className={`px-4 py-3 rounded-xl border-2 font-display font-semibold transition-all ${
                      active ? "border-[#0C4A6E] bg-[#0C4A6E] text-white" : "border-slate-200 text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    {b}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Keep the address selector mounted to retain the selected snapshot between steps. */}
        <div hidden={step !== 2}>
          <BookingAddress token={token} data={data} cities={CITIES} onChange={update} onReady={setAddressReady} />
        </div>

        {/* STEP 6: Date & Slot */}
        {step === 3 && (
          <div data-testid="step-6">
            <h2 className="font-display font-bold text-xl text-slate-900">Pick a date & time slot</h2>
            <p className="text-sm text-slate-500 mt-1">Choose your preferred date and time. Your selection will be highlighted.</p>

            <div className="mt-5">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" /> Date
              </label>
              <div className="mt-2 grid grid-cols-4 sm:grid-cols-7 gap-2">
                {dateOptions().map((d) => {
                  const active = data.date === d.iso;
                  return (
                    <button
                      key={d.iso}
                      data-testid={`booking-date-${d.iso}`}
                      aria-pressed={active}
                      onClick={() => update({ date: d.iso })}
                      className={`p-2 rounded-xl border-2 text-center transition-all ${dateBtnClass(active)}`}
                    >
                      <div className="text-[10px] font-semibold uppercase">{d.label || d.day}</div>
                      <div className="font-display font-bold text-lg">{d.dnum}</div>
                      <div className="text-[10px]">{d.month}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Slot
              </label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SLOTS.map((s) => {
                  const Icon = slotIcon(s.id);
                  const active = data.slot === s.id;
                  return (
                    <button
                      key={s.id}
                      data-testid={`booking-slot-${s.id}`}
                      aria-pressed={active}
                      onClick={() => update({ slot: s.id })}
                      className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${slotBtnClass(active)}`}
                    >
                      <Icon className={`w-5 h-5 ${slotIconClass(active)}`} />
                      <div>
                        <div className="font-semibold text-sm">{s.label}</div>
                        <div className={`text-xs ${active ? "text-slate-200" : "text-slate-500"}`}>{s.time}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* STEP 7: Contact */}
        {step === 2 && (
          <div data-testid="step-7">
            <h2 className="font-display font-bold text-xl text-slate-900">Your contact details</h2>
            <p className="text-sm text-slate-500 mt-1">So our technician can reach you on the day.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label htmlFor="booking-name" className="text-sm font-medium text-slate-700">Full name *</label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    id="booking-name"
                    data-testid="booking-name"
                    value={data.name}
                    onChange={(e) => update({ name: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#0C4A6E] focus:ring-2 focus:ring-[#0C4A6E]/10 outline-none"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="booking-phone" className="text-sm font-medium text-slate-700">Phone (10-digit) *</label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  {customer?.phone ? (
                    <input
                      id="booking-phone"
                    data-testid="booking-phone"
                      inputMode="numeric"
                      value={data.phone}
                      readOnly
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-100 text-slate-700 outline-none cursor-not-allowed"
                    />
                  ) : (
                    <input
                      id="booking-phone"
                    data-testid="booking-phone"
                      inputMode="numeric"
                      value={data.phone}
                      onChange={(e) => update({ phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#0C4A6E] focus:ring-2 focus:ring-[#0C4A6E]/10 outline-none"
                    />
                  )}
                </div>
                {customer?.phone && (
                  <p className="text-xs text-slate-500 mt-2">
                    Using your registered mobile number from login.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="booking-email" className="text-sm font-medium text-slate-700">Email (optional)</label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    id="booking-email"
                    data-testid="booking-email"
                    value={data.email}
                    onChange={(e) => update({ email: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#0C4A6E] focus:ring-2 focus:ring-[#0C4A6E]/10 outline-none"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="booking-notes" className="text-sm font-medium text-slate-700">Notes (optional)</label>
                <textarea
                  id="booking-notes"
                    data-testid="booking-notes"
                  rows={3}
                  placeholder="Anything specific our technician should know?"
                  value={data.notes}
                  onChange={(e) => update({ notes: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0C4A6E] focus:ring-2 focus:ring-[#0C4A6E]/10 outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 8: Review */}
        {step === 4 && service && category && (
          <div data-testid="step-8">
            <h2 className="font-display font-bold text-xl text-slate-900">Review & confirm</h2>
            <p className="text-sm text-slate-500 mt-1">Booking is free. Pay after job completion.</p>

            <div className="mt-5 bg-slate-50 rounded-xl p-5 space-y-3 text-sm">
              <Row k="Service" v={`${service.name} (${category.short})`} />
              <Row k="Brand" v={data.brand} />
              <Row k="Customer type" v={SEGMENT_OPTIONS.find(s => s.id === data.customer_type)?.name} />
              <Row k="Date & slot" v={`${data.date} • ${SLOTS.find(s => s.id === data.slot)?.label}`} />
              <Row k="Address" v={`${data.address}, ${CITIES.find(c => c.id === data.city)?.name}${data.pincode ? " - " + data.pincode : ""}`} />
              {data.landmark && <Row k="Landmark" v={data.landmark} />}
              <Row k="Contact" v={`${data.name} • ${data.phone}${data.email ? " • " + data.email : ""}`} />
            </div>

            <div className="mt-5 rounded-xl border-2 border-dashed border-[#0369A1]/40 bg-[#FFF1EA] p-5 flex justify-between items-center">
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase">Estimated charge</div>
                <div className="font-display font-extrabold text-3xl text-[#0C4A6E] mt-1">
                  <ServicePrice service={service} />{service.id.includes("amc") && <span className="text-base font-medium text-slate-500">/yr</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1">+ spares at MRP (if needed, with your approval)</div>
              </div>
              <Sparkles className="w-10 h-10 text-[#0369A1]" />
            </div>
          </div>
        )}

        {validation && <p role="alert" className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">{validation}</p>}

        {/* Navigation */}
        <div className="mt-8 pt-6 border-t border-slate-100 flex justify-between gap-3">
          <button
            data-testid="step-back-btn"
            onClick={back}
            disabled={step === 1 || submitting}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < TOTAL ? (
            <button
              data-testid="step-next-btn"
              onClick={next}
              disabled={catalogLoading || !!catalogError}
              className="px-6 py-2.5 rounded-xl bg-[#0C4A6E] hover:bg-[#132D53] text-white font-semibold flex items-center gap-2"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="step-confirm-btn"
              onClick={submit}
              disabled={submitting || catalogLoading || !!catalogError}
              className="px-6 py-2.5 rounded-xl bg-[#0369A1] hover:bg-[#075985] text-white font-semibold flex items-center gap-2 disabled:opacity-60"
            >
              {submitting ? "Confirming..." : "Confirm Booking"} <Check className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-slate-500">{k}</span>
    <span className="font-semibold text-slate-900 text-right">{v}</span>
  </div>
);
